import {useCallback} from "react";
import {toast, UpdateOptions} from "react-toastify";
import {toastSetting} from "../toastSettingObject";
import {useTranslation} from "react-i18next";

export const downloadBlob = (blob: Blob, fileName: string): void => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = downloadUrl;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

export const downloadFromUrl = async (url: string, fileName: string): Promise<void> => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.statusText}`);
  }

  const blob = await response.blob();
  downloadBlob(blob, fileName);
};

export const useDownload = () => {
  const download = useCallback(async (url: string, fileName: string): Promise<void> => {
    await downloadFromUrl(url, fileName);
  }, []);

  return {download};
};

export const useDownloadWithToast = () => {
  const {t} = useTranslation();
  const {download} = useDownload();

  const downloadWithToast = useCallback(
    async (url: string, fileName: string): Promise<void> => {
      const toastId = toast.loading(t("export.downloading"), {
        position: "bottom-right",
      });

      try {
        await download(url, fileName);

        toast.update(toastId, {
          render: t("download.success"),
          ...(toastSetting.success as UpdateOptions),
        });
      } catch (error) {
        toast.update(toastId, {
          render: t("download.error"),
          ...(toastSetting.error as UpdateOptions),
        });

        throw error;
      }
    },
    [download, t],
  );

  return {download: downloadWithToast};
};
